/**
 * The audio and video devices a user picked for calls, plus related call
 * settings, saved in this browser under `telehealth_device_prefs`.
 *
 * Phone Settings, the top-bar phone menu, video visits and the phone service
 * all read and write this one object. Backwards compatible with what those
 * screens saved before: same key and field names, unknown fields are kept,
 * and a missing or malformed value falls back to its old default.
 */
import { z } from 'zod'
import { BrowserStore } from '@/utils/BrowserStore'

export const DEVICE_PREFS_KEY = 'telehealth_device_prefs'

const deviceId = z.string().catch('').default('')

const schema = z.looseObject({
  audioDeviceId: deviceId,
  // Device names let us find a headset again when the browser gives it a new
  // id (Windows does this, e.g. after a driver update or re-pairing).
  audioDeviceLabel: deviceId,
  audioOutputDeviceId: deviceId,
  audioOutputDeviceLabel: deviceId,
  videoDeviceId: deviceId,
  blurEnabled: z.boolean().optional().catch(undefined),
  noiseSuppression: z.boolean().catch(true).default(true),
})

const store = new BrowserStore(DEVICE_PREFS_KEY, schema)

export function loadDevicePrefs() {
  return store.load()
}

/**
 * Saves only the given fields, keeping everything else (including fields
 * this version doesn't know). Skips the write when nothing changes: every
 * write notifies the other tabs, which re-apply devices in response.
 */
export function updateDevicePrefs(changes) {
  const current = store.load()
  const next = { ...current, ...changes }
  if (Object.keys(changes).every((key) => current[key] === next[key])) return current
  store.save(next)
  return next
}

// The browser's own "Default - …" / "Communications - …" entries follow the
// system setting; they're never the device someone picked by name.
const SYSTEM_ENTRIES = new Set(['default', 'communications'])

/**
 * Finds the saved device among the connected ones: by id, else by name.
 * `devices` is any iterable of objects with `deviceId` and `label`
 * (MediaDeviceInfo, or the values of the Voice SDK's device maps).
 * Returns the device, or null when it isn't connected.
 */
export function findSavedDevice(devices, savedId, savedLabel) {
  const list = [...(devices || [])]
  if (savedId) {
    const byId = list.find((d) => d.deviceId === savedId)
    if (byId) return byId
  }
  if (savedLabel) {
    const byLabel = list.find((d) => d.label === savedLabel && !SYSTEM_ENTRIES.has(d.deviceId))
    if (byLabel) return byLabel
  }
  return null
}
